/**
 * Drug ID Number Extraction for IXVerify
 * Extracts potential drug IDs from OCR text using pattern matching
 */

export interface ExtractedNumber {
  value: string;
  confidence: 'high' | 'medium' | 'low';
  type: 'drug_id' | 'lot_number' | 'expiry' | 'unknown';
  contextMatch?: string;
}

// Clinical/IXRS keywords that typically appear near med ID numbers
const CLINICAL_KEYWORDS = [
  'MED ID NUMBER', 'MED ID', 'MED NUMBER', 'MED #', 'MED NO',
  'MEDICATION ID', 'MEDICATION NUMBER', 'MEDICATION #',
  'DRUG ASSIGNED', 'DRUG DESCRIPTION',
  'PACK', 'KIT', 'KIT NUMBER', 'KIT #', 'KIT NO',
  'PACK NUMBER', 'PACK #', 'PACK NO', 'PACK DISPENSED',
  'DRUG', 'DRUG #', 'DRUG NO', 'DRUG NUMBER', 'DRUG ID',
  'MED', 'MED DISPENSED',
  'IP NUMBER', 'IP #', 'IP NO', 'IP DISPENSED', 'IP ID',
  'INVESTIGATIONAL PRODUCT', 'IP',
  'QUANTITY OF LOT', 'LOT NUMBER', 'LOT #', 'LOT NO', 'LOT',
  'BATCH NUMBER', 'BATCH #', 'BATCH',
  'ID NUMBER', 'ID #', 'ID NO', 'ID',
  'DISPENSED', 'DISPENSE', 'DISPENSING', 'ASSIGNED',
  'RANDOMIZATION', 'RANDOMIZATION #', 'RANDOMIZATION NO', 'RAND #',
  'RANDOMIZATION DAY',
  'IXRS', 'IRT', 'IWRS', 'RTSM', 'ENDPOINT',
  'BOTTLE', 'BOTTLE #', 'BOTTLE NO', 'BOTTLE NUMBER',
  'VIAL', 'VIAL #', 'VIAL NO', 'VIAL NUMBER',
  'SUBJECT', 'PATIENT', 'PARTICIPANT NUMBER', 'PARTICIPANT',
  'SITE NUMBER', 'SITE'
];

/**
 * Find numbers that appear near clinical keywords in the text
 */
function findContextualNumbers(ocrText: string): ExtractedNumber[] {
  const results: ExtractedNumber[] = [];
  const seenValues = new Set<string>();
  const upperText = ocrText.toUpperCase();

  for (const keyword of CLINICAL_KEYWORDS) {
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const patterns = [
      new RegExp(`${escapedKeyword}[\\s:=#]*([A-Z0-9][A-Z0-9\\-]{3,11})`, 'gi'),
      new RegExp(`${escapedKeyword}[\\s:=#]*\\n\\s*([A-Z0-9][A-Z0-9\\-]{3,11})`, 'gi'),
    ];

    for (const regex of patterns) {
      let match;
      regex.lastIndex = 0;

      while ((match = regex.exec(upperText)) !== null) {
        const value = match[1].toUpperCase().trim();

        if (seenValues.has(value)) continue;
        if (value.length < 4) continue;
        if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]?\d{0,4}$/.test(value)) continue;
        if (/^\d{1,2}:\d{2}/.test(value)) continue;
        if (isLikelyNoise(value)) continue;

        seenValues.add(value);
        results.push({
          value,
          confidence: 'high',
          type: 'drug_id',
          contextMatch: keyword
        });
      }
    }
  }

  return results;
}

/**
 * Check if a value is likely noise (not a drug ID)
 */
function isLikelyNoise(value: string): boolean {
  const skipPatterns = [
    /^(THE|AND|FOR|THIS|THAT|WITH|FROM|PAGE|TOTAL|DATE|TIME|YES|NO)$/i,
    /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)/i,
    /^(MON|TUE|WED|THU|FRI|SAT|SUN)/i,
    /^\d{5}(-\d{4})?$/,
    /^\d{3}[-.]?\d{3}[-.]?\d{4}$/,
    /^(HTTP|WWW|COM|ORG|NET)/i,
    /^(AM|PM)$/i,
    /^V?\d+\.\d+/,
    /^US\d{2,4}$/i,
    /^US\d{7,}$/i,
    /^MRNA[-]?\d{4}[-]?P\d{2,4}$/i,
    /^\d{1,3}$/,
    /^(19|20)\d{2}$/,
    /^(MG|ML|MCG|IU|KG|LB|NA|NR|TBD|TBA)$/i,
    /@/,
    /^\d+%$/,
  ];

  return skipPatterns.some(pattern => pattern.test(value));
}

/**
 * Extract potential drug ID numbers from OCR text
 */
export function extractNumbers(ocrText: string): ExtractedNumber[] {
  if (!ocrText) return [];

  const results: ExtractedNumber[] = [];
  const seenValues = new Set<string>();

  // First: Find context-aware matches
  const contextualMatches = findContextualNumbers(ocrText);
  for (const match of contextualMatches) {
    if (!seenValues.has(match.value)) {
      seenValues.add(match.value);
      results.push(match);
    }
  }

  // Second: Apply general patterns
  const patterns = [
    { regex: /\b(Y\d{9})\b/gi, type: 'lot_number' as const, confidence: 'high' as const },
    { regex: /\b(BX\d{8})\b/gi, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b(PA\d{7})\b/gi, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b(LLY\d{6})\b/gi, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b([A-Z]{2,3}\d{6,9})\b/gi, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b(\d{4,5}[-\s]?\d{3,4}[-\s]?\d{2})\b/g, type: 'drug_id' as const, confidence: 'medium' as const },
    { regex: /(?:LOT|BATCH)[\s:#]*([A-Z0-9][-A-Z0-9]{4,15})/gi, type: 'lot_number' as const, confidence: 'medium' as const },
    { regex: /\b([A-Z]{1,4}[-]?\d{4,10})\b/gi, type: 'drug_id' as const, confidence: 'medium' as const },
    { regex: /\b(\d{4,10}[-]?[A-Z]{1,4})\b/gi, type: 'drug_id' as const, confidence: 'medium' as const },
    { regex: /\b([A-Z0-9]{2,4}[-][A-Z0-9]{2,6}(?:[-][A-Z0-9]{2,4})?)\b/gi, type: 'drug_id' as const, confidence: 'medium' as const },
    { regex: /\b(\d{6})\b/g, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b(\d{7,8})\b/g, type: 'drug_id' as const, confidence: 'high' as const },
    { regex: /\b(\d{9,12})\b/g, type: 'drug_id' as const, confidence: 'medium' as const },
    { regex: /\b(\d{4,5})\b/g, type: 'drug_id' as const, confidence: 'low' as const },
    { regex: /(?:EXP|EXPIRY|EXPIRES?)[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{1,2}[\/\-]\d{2,4})/gi, type: 'expiry' as const, confidence: 'low' as const }
  ];

  for (const pattern of patterns) {
    let match;
    pattern.regex.lastIndex = 0;

    while ((match = pattern.regex.exec(ocrText)) !== null) {
      const value = match[1].toUpperCase().trim();

      if (seenValues.has(value)) continue;
      if (value.length < 4) continue;
      if (isLikelyNoise(value)) continue;

      const hasLetters = /[A-Z]/.test(value);
      const hasNumbers = /[0-9]/.test(value);
      if (hasLetters && !hasNumbers) continue;

      seenValues.add(value);
      results.push({
        value,
        confidence: pattern.confidence,
        type: pattern.type
      });
    }
  }

  // Sort by confidence then length
  results.sort((a, b) => {
    const confidenceOrder = { high: 0, medium: 1, low: 2 };
    const confDiff = confidenceOrder[a.confidence] - confidenceOrder[b.confidence];
    if (confDiff !== 0) return confDiff;
    return b.value.length - a.value.length;
  });

  return results;
}

/**
 * Compare two drug IDs to check if they match
 */
export function compareNumbers(num1: string, num2: string): {
  match: boolean;
  similarity: number;
} {
  const normalize = (s: string) => s.toUpperCase().replace(/[\s\-\.]/g, '');

  const n1 = normalize(num1);
  const n2 = normalize(num2);

  if (n1 === n2) {
    return { match: true, similarity: 1 };
  }

  const distance = levenshteinDistance(n1, n2);
  const maxLen = Math.max(n1.length, n2.length);
  const similarity = maxLen > 0 ? 1 - distance / maxLen : 0;

  const threshold = n1.length > 6 ? 0.9 : 1;
  const match = similarity >= threshold;

  return { match, similarity };
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(s1: string, s2: string): number {
  const m = s1.length;
  const n = s2.length;

  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
    }
  }

  return dp[m][n];
}
